import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, LogOut, Plus, Clock, CheckCircle, XCircle, 
  MapPin, Calendar, DollarSign, User, Menu, X, FileText,
  Edit2, Trash2, Send, Info, Eye
} from "lucide-react";
import { LeadPhotoGallery } from "../components/LeadPhotoGallery";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

// Labor-only disclaimer
const LABOR_ONLY_DISCLAIMER = "This estimate reflects labor costs only. Materials are not included and will be quoted by the contractor.";

export default function HomeownerDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobRequests, setJobRequests] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.tab || "requests");

  const fetchJobRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/job-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobRequests(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchQuotes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    }
  }, [token]);

  // Link pending quote to user account
  const linkPendingQuote = useCallback(async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/link`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Your quote has been linked to your account!");
      fetchQuotes(); // Refresh quotes
      setActiveTab("quotes");
    } catch (error) {
      // Don't show error to user - quote might already be linked
    }
  }, [token, fetchQuotes]);

  const linkQuoteIdFromNav = location.state?.linkQuoteId;

  useEffect(() => {
    fetchJobRequests();
    fetchQuotes();
    
    // Check for pending quote to link
    const sessionQuoteId = sessionStorage.getItem("pendingQuoteLink");
    
    if (linkQuoteIdFromNav || sessionQuoteId) {
      linkPendingQuote(linkQuoteIdFromNav || sessionQuoteId);
      if (sessionQuoteId) {
        sessionStorage.removeItem("pendingQuoteLink");
      }
    }
  }, [fetchJobRequests, fetchQuotes, linkPendingQuote, linkQuoteIdFromNav]);

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    
    try {
      await axios.delete(`${API}/quotes/${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deleted");
      fetchQuotes();
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };
  
  // Convert quote to lead
  const handleConvertToLead = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/convert-to-lead`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote submitted to contractors!");
      fetchQuotes();
      fetchJobRequests();
      setActiveTab("requests");
    } catch (error) {
      toast.error("Failed to submit quote to contractors");
    }
  };

  const handleEditQuote = (quote) => {
    navigate(`/request/${quote.category_id}`, {
      state: {
        preservedAnswers: quote.answers,
        preservedZipCode: quote.zip_code
      }
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      matched: "bg-blue-100 text-blue-800",
      in_progress: "bg-[#0FA3B1]/15 text-[#0FA3B1]",
      completed: "bg-[#7ED957]/20 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return styles[status] || styles.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatPrice = (low, high) => {
    if (!low && !high) return "Pending";
    return `$${low?.toLocaleString() || 0} – $${high?.toLocaleString() || 0}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="homeowner-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#0FA3B1] flex items-center justify-center">
            <Home className="w-4 h-4 text-white" />
          </div>
          <span className="font-manrope font-bold text-[#1F2933]">Contractor Hub LLC</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-white">Contractor Hub LLC</span>
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 p-3 bg-white/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-[#0FA3B1] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">{user?.name}</p>
                <p className="text-white/60 text-xs">Homeowner</p>
              </div>
            </div>
          </div>

          <nav className="space-y-1">
            <button className="sidebar-link active w-full text-left">
              <FileText className="w-5 h-5" /> My Requests
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-300 hover:bg-red-500/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-[260px] p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-manrope font-bold text-2xl lg:text-3xl text-[#1F2933]">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-[#1F2933]/60">Manage your service requests</p>
          </div>
          
          <Button 
            onClick={() => navigate('/submit-request')}
            className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full px-6"
            data-testid="new-request-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> New Request
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Requests", value: jobRequests.length, color: "bg-[#0FA3B1]" },
            { label: "Pending", value: jobRequests.filter(j => j.status === 'pending').length, color: "bg-yellow-500" },
            { label: "In Progress", value: jobRequests.filter(j => j.status === 'in_progress').length, color: "bg-blue-500" },
            { label: "Saved Quotes", value: quotes.length, color: "bg-purple-500" }
          ].map((stat) => (
            <div key={stat.label} className="stat-card">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}>
                <span className="text-white font-bold">{stat.value}</span>
              </div>
              <p className="text-sm text-[#1F2933]/60">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("requests")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "requests" 
                ? "bg-[#0FA3B1] text-white" 
                : "bg-gray-100 text-[#1F2933]/70 hover:bg-gray-200"
            }`}
            data-testid="requests-tab"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            My Requests ({jobRequests.length})
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "quotes" 
                ? "bg-[#0FA3B1] text-white" 
                : "bg-gray-100 text-[#1F2933]/70 hover:bg-gray-200"
            }`}
            data-testid="quotes-tab"
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Saved Quotes ({quotes.length})
          </button>
        </div>

        {/* Requests Tab Content */}
        {activeTab === "requests" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-manrope font-semibold text-lg text-[#1F2933]">Your Requests</h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : jobRequests.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-[#1F2933] mb-2">No requests yet</h3>
              <p className="text-[#1F2933]/60 mb-6">Submit your first service request to get started</p>
              <Button 
                onClick={() => navigate('/submit-request')}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" /> Submit Request
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {jobRequests.map((request) => (
                <div 
                  key={request.request_id} 
                  className="p-6 hover:bg-gray-50 transition-colors"
                  data-testid={`request-${request.request_id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#1F2933]">{request.title}</h3>
                        <Badge className={getStatusBadge(request.status)}>
                          {getStatusIcon(request.status)}
                          <span className="ml-1 capitalize">{request.status.replace('_', ' ')}</span>
                        </Badge>
                      </div>
                      <p className="text-sm text-[#1F2933]/60 mb-3 line-clamp-2">
                        {request.description}
                      </p>
                      <div className="mb-3">
                        <LeadPhotoGallery leadId={request.request_id} token={token} compact />
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#1F2933]/70">
                        <span className="flex items-center gap-1">
                          <span className="w-6 h-6 rounded bg-[#0FA3B1]/10 flex items-center justify-center">
                            <FileText className="w-3 h-3 text-[#0FA3B1]" />
                          </span>
                          {request.category_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#1F2933]/40" />
                          {request.address}
                        </span>
                        {request.preferred_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-[#1F2933]/40" />
                            {request.preferred_date}
                          </span>
                        )}
                        {request.budget_range && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-[#1F2933]/40" />
                            {request.budget_range}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#1F2933]/50">
                        {new Date(request.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-[#0FA3B1] font-medium">
                        {request.num_contractors} contractor{request.num_contractors > 1 ? 's' : ''} requested
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Quotes Tab Content */}
        {activeTab === "quotes" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="font-manrope font-semibold text-lg text-[#1F2933]">Saved Quotes</h2>
              <Button 
                onClick={() => navigate('/request')}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full text-sm"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" /> New Quote
              </Button>
            </div>
          </div>

          {/* Labor-Only Disclaimer */}
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-amber-800 text-xs">{LABOR_ONLY_DISCLAIMER}</p>
          </div>

          {quotes.length === 0 ? (
            <div className="text-center py-16 px-6">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-[#1F2933] mb-2">No saved quotes</h3>
              <p className="text-[#1F2933]/60 mb-6">Get a quick estimate without contacting contractors</p>
              <Button 
                onClick={() => navigate('/request')}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" /> Get a Quote
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {quotes.map((quote) => (
                <div 
                  key={quote.quote_id} 
                  className="p-6 hover:bg-gray-50 transition-colors"
                  data-testid={`quote-${quote.quote_id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#1F2933]">{quote.category_name}</h3>
                        <Badge className="bg-purple-100 text-purple-800">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Quote
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#1F2933]/70 mb-2">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-[#1F2933]/40" />
                          {quote.zip_code}
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-[#1F2933]/40" />
                          {formatPrice(quote.estimated_min, quote.estimated_max)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-[#1F2933]/40" />
                          {formatDate(quote.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-amber-600">Labor-only estimate</p>
                      {/* Custom request (if provided) */}
                      {quote.answers?.unlisted_service_description && (
                        <p className="text-xs text-[#0FA3B1] mt-1 line-clamp-2" data-testid="quote-unlisted-desc">
                          <span className="font-medium">Custom Request:</span> {quote.answers.unlisted_service_description}
                        </p>
                      )}
                      {/* Additional Details (if provided) */}
                      {quote.answers?.additional_details && (
                        <p className="text-xs text-blue-600 mt-1 line-clamp-2">
                          <span className="font-medium">Additional Notes:</span> {quote.answers.additional_details}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditQuote(quote)}
                        className="text-[#1F2933]/70"
                        data-testid={`edit-quote-${quote.quote_id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigate('/quote-summary', {
                            state: {
                              quoteData: {
                                category: quote.category_id,
                                categoryName: quote.category_name,
                                answers: quote.answers,
                                estimatedPrice: { low: quote.estimated_min, high: quote.estimated_max },
                                zipCode: quote.zip_code
                              }
                            }
                          });
                        }}
                        className="text-[#1F2933]/70"
                        data-testid={`view-quote-${quote.quote_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConvertToLead(quote.quote_id)}
                        className="text-[#0FA3B1] border-[#0FA3B1] hover:bg-[#0FA3B1] hover:text-white"
                        data-testid={`submit-quote-${quote.quote_id}`}
                      >
                        <Send className="w-4 h-4 mr-1" /> Send to Contractors
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteQuote(quote.quote_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-quote-${quote.quote_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
